import React from 'react'
import QuestionCard from './QuestionCard'
import RadioOption from './RadioOption'
import CheckboxOption from './CheckboxOption'

interface SectionProps {
  formData: Record<string, any>
  updateField: (field: string, value: any) => void
}

/**
 * Section A — Profile (Q1-Q4). Shown to all respondents.
 * Collects annual revenue, team size, main vertical, and platforms used.
 */
const SectionA: React.FC<SectionProps> = ({ formData, updateField }) => {
  const handleCheckboxChange = (field: string, value: string, checked: boolean) => {
    const current: string[] = formData[field] || []
    const updated = checked
      ? [...current, value]
      : current.filter((v: string) => v !== value)
    updateField(field, updated)
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Q1 — Faturamento anual */}
      <QuestionCard title="Qual o faturamento anual do seu e-commerce?" required>
        <RadioOption
          name="q1"
          value="ate_500k"
          label="Até R$500k"
          selected={formData.q1 === 'ate_500k'}
          onChange={(v) => updateField('q1', v)}
        />
        <RadioOption
          name="q1"
          value="500k_2m"
          label="R$500k - R$2M"
          selected={formData.q1 === '500k_2m'}
          onChange={(v) => updateField('q1', v)}
        />
        <RadioOption
          name="q1"
          value="2m_10m"
          label="R$2M - R$10M"
          selected={formData.q1 === '2m_10m'}
          onChange={(v) => updateField('q1', v)}
        />
        <RadioOption
          name="q1"
          value="10m_50m"
          label="R$10M - R$50M"
          selected={formData.q1 === '10m_50m'}
          onChange={(v) => updateField('q1', v)}
        />
        <RadioOption
          name="q1"
          value="50m_200m"
          label="R$50M - R$200M"
          selected={formData.q1 === '50m_200m'}
          onChange={(v) => updateField('q1', v)}
        />
        <RadioOption
          name="q1"
          value="acima_200m"
          label="Acima de R$200M"
          selected={formData.q1 === 'acima_200m'}
          onChange={(v) => updateField('q1', v)}
        />
      </QuestionCard>

      {/* Q2 — Tamanho da equipe */}
      <QuestionCard
        title="Quantas pessoas trabalham na operação de e-commerce?"
        required
      >
        <RadioOption
          name="q2"
          value="1_2"
          label="1-2 (eu e mais alguém)"
          selected={formData.q2 === '1_2'}
          onChange={(v) => updateField('q2', v)}
        />
        <RadioOption
          name="q2"
          value="3_5"
          label="3-5"
          selected={formData.q2 === '3_5'}
          onChange={(v) => updateField('q2', v)}
        />
        <RadioOption
          name="q2"
          value="6_15"
          label="6-15"
          selected={formData.q2 === '6_15'}
          onChange={(v) => updateField('q2', v)}
        />
        <RadioOption
          name="q2"
          value="16_50"
          label="16-50"
          selected={formData.q2 === '16_50'}
          onChange={(v) => updateField('q2', v)}
        />
        <RadioOption
          name="q2"
          value="mais_50"
          label="Mais de 50"
          selected={formData.q2 === 'mais_50'}
          onChange={(v) => updateField('q2', v)}
        />
      </QuestionCard>

      {/* Q3 — Vertical principal */}
      <QuestionCard title="Qual sua principal vertical?" required>
        <RadioOption
          name="q3"
          value="moda"
          label="Moda e acessórios"
          selected={formData.q3 === 'moda'}
          onChange={(v) => updateField('q3', v)}
        />
        <RadioOption
          name="q3"
          value="beleza"
          label="Beleza e cosméticos"
          selected={formData.q3 === 'beleza'}
          onChange={(v) => updateField('q3', v)}
        />
        <RadioOption
          name="q3"
          value="casa"
          label="Casa e decoração"
          selected={formData.q3 === 'casa'}
          onChange={(v) => updateField('q3', v)}
        />
        <RadioOption
          name="q3"
          value="eletronicos"
          label="Eletrônicos e tecnologia"
          selected={formData.q3 === 'eletronicos'}
          onChange={(v) => updateField('q3', v)}
        />
        <RadioOption
          name="q3"
          value="alimentos"
          label="Alimentos e bebidas"
          selected={formData.q3 === 'alimentos'}
          onChange={(v) => updateField('q3', v)}
        />
        <RadioOption
          name="q3"
          value="saude"
          label="Saúde e bem-estar"
          selected={formData.q3 === 'saude'}
          onChange={(v) => updateField('q3', v)}
        />
        <RadioOption
          name="q3"
          value="outro"
          label="Outro"
          selected={formData.q3 === 'outro'}
          onChange={(v) => updateField('q3', v)}
          hasOtherField
          otherValue={formData.q3_other || ''}
          onOtherChange={(v) => updateField('q3_other', v)}
        />
      </QuestionCard>

      {/* Q4 — Plataformas */}
      <QuestionCard title="Quais plataformas você usa?" required>
        <CheckboxOption
          name="q4"
          value="nuvemshop"
          label="Nuvemshop"
          checked={(formData.q4 || []).includes('nuvemshop')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
        />
        <CheckboxOption
          name="q4"
          value="vtex"
          label="VTEX"
          checked={(formData.q4 || []).includes('vtex')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
        />
        <CheckboxOption
          name="q4"
          value="shopify"
          label="Shopify"
          checked={(formData.q4 || []).includes('shopify')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
        />
        <CheckboxOption
          name="q4"
          value="tray"
          label="Tray"
          checked={(formData.q4 || []).includes('tray')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
        />
        <CheckboxOption
          name="q4"
          value="loja_integrada"
          label="Loja Integrada"
          checked={(formData.q4 || []).includes('loja_integrada')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
        />
        <CheckboxOption
          name="q4"
          value="mercado_livre"
          label="Mercado Livre (como seller)"
          checked={(formData.q4 || []).includes('mercado_livre')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
        />
        <CheckboxOption
          name="q4"
          value="shopee"
          label="Shopee"
          checked={(formData.q4 || []).includes('shopee')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
        />
        <CheckboxOption
          name="q4"
          value="amazon_br"
          label="Amazon BR"
          checked={(formData.q4 || []).includes('amazon_br')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
        />
        <CheckboxOption
          name="q4"
          value="magalu"
          label="Magalu Marketplace"
          checked={(formData.q4 || []).includes('magalu')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
        />
        <CheckboxOption
          name="q4"
          value="tiktok_shop"
          label="TikTok Shop"
          checked={(formData.q4 || []).includes('tiktok_shop')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
        />
        <CheckboxOption
          name="q4"
          value="outro"
          label="Outro"
          checked={(formData.q4 || []).includes('outro')}
          onChange={(v, c) => handleCheckboxChange('q4', v, c)}
          hasOtherField
          otherValue={formData.q4_other || ''}
          onOtherChange={(v) => updateField('q4_other', v)}
        />
      </QuestionCard>
    </div>
  )
}

export default SectionA
